'use client'

export const dynamic = 'force-dynamic'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { useParams } from 'next/navigation'
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  HandHeart,
  MapPin,
  Phone,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react'
import {
  AIAlert,
  Badge,
  Button,
  ButtonLink,
  Card,
  StatusBadge,
} from '../../../../components/ui'
// H3: no demo-data — the volunteer form derives its header from the slug.
import { useRequireAuth } from '../../../../components/auth/AuthGate'
import type { VolunteerSkill } from '../../../../lib/masjid-volunteers-store'

const skillOptions: Array<{ value: VolunteerSkill; label: string; helper: string }> = [
  { value: 'event_support', label: 'Event support', helper: 'Classes, lectures, Eid, community events' },
  { value: 'cleaning', label: 'Cleaning', helper: 'Masjid clean-up and facility upkeep' },
  { value: 'teaching', label: 'Teaching', helper: 'Quran, basics, youth mentoring' },
  { value: 'fundraising', label: 'Fundraising', helper: 'Campaign outreach and donor support' },
  { value: 'media', label: 'Media', helper: 'Photos, announcements, digital updates' },
  { value: 'crowd_management', label: 'Crowd management', helper: 'Jumuah, Ramadan, Eid flow support' },
  { value: 'ramadan_support', label: 'Ramadan support', helper: 'Iftar, Taraweeh, volunteer shifts' },
  { value: 'janaza_support', label: 'Funeral prayer support', helper: 'Urgent community response' },
  { value: 'other', label: 'Other', helper: 'Any other useful support' },
]

const initialForm = {
  name: '',
  mobile: '',
  email: '',
  locality: '',
  skills: [] as VolunteerSkill[],
  availabilityNote: '',
  consent: false,
}

function normalizeMasjid(slug: string): any {
  // H3: no demo masjid. Derive a neutral header from the slug (no fabricated name/data).
  // TODO(follow-up): fetch the real masjid by slug like the profile page does.
  return { slug, nameEnglish: slug, city: null, district: null }
}

export default function MasjidVolunteerPage() {
  const params = useParams<{ slug: string }>()
  const requireAuth = useRequireAuth()
  const masjid = useMemo(() => normalizeMasjid(params.slug), [params.slug])
  const [form, setForm] = useState(initialForm)

  // Stage 5a: pre-fill name/phone/email from the signed-in account (GET /api/me → 401/404 for guests,
  // so this no-ops for them). Only fills EMPTY fields, so it never clobbers what the user typed. The
  // submission's account linkage is set server-side from the session; this is convenience only, and the
  // masjid-operational task selection below is unchanged.
  useEffect(() => {
    let alive = true
    fetch('/api/me', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !d?.user) return
        setForm((f) => ({
          ...f,
          name: f.name || d.user.name || '',
          mobile: f.mobile || (d.user.phone ? String(d.user.phone) : ''),
          email: f.email || d.user.email || '',
        }))
      })
      .catch(() => {})
    return () => { alive = false }
  }, [])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [volunteerId, setVolunteerId] = useState('')

  function setField(key: keyof typeof initialForm, value: string | boolean | VolunteerSkill[]) {
    setForm((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: '' }))
  }

  function toggleSkill(skill: VolunteerSkill) {
    setForm((prev) => {
      const next = prev.skills.includes(skill)
        ? prev.skills.filter((item) => item !== skill)
        : [...prev.skills, skill]
      return { ...prev, skills: next }
    })
    setErrors((prev) => ({ ...prev, skills: '' }))
  }

  async function submit() {
    setSubmitting(true)
    setErrors({})
    try {
      const res = await fetch('/api/masjid-volunteers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          masjidId: String((masjid as any).id),
          masjidSlug: String((masjid as any).slug),
          masjidName: String((masjid as any).nameEnglish),
          name: form.name,
          mobile: form.mobile,
          email: form.email,
          locality: form.locality,
          skills: form.skills,
          availabilityNote: form.availabilityNote,
          consent: form.consent,
          source: 'public_profile',
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setErrors(data.errors || { submit: data.error || 'Volunteer registration failed.' })
        return
      }
      setVolunteerId(data.volunteer?.id || '')
      setSubmitted(true)
    } catch {
      setErrors({ submit: 'Could not reach the volunteer service. Please try again.' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="entry-page">
      <div className="entry-shell">
        <div className="entry-layout">
          <section className="entry-card">
            <Link href={`/masjids/${(masjid as any).slug}`} className="entry-kicker"><ArrowLeft size={14} /> Back to profile</Link>
            <div>
              <h1 className="entry-title">Volunteer with this masjid.</h1>
              <p className="entry-lede">
                Register your skills and availability privately. Committee operators can follow up, assign activities, and coordinate service work through MasjidOS.
              </p>
            </div>

            <Card padding="lg" className="entry-card">
              <div className="entry-card-head">
                <div>
                  <span className="entry-kicker"><HandHeart size={14} /> Community CRM</span>
                  <h2>{submitted ? 'Registration received' : (masjid as any).nameEnglish}</h2>
                </div>
                <StatusBadge tone={submitted ? 'emerald' : 'info'}>{submitted ? 'New volunteer' : 'Private intake'}</StatusBadge>
              </div>

              {submitted ? (
                <div className="entry-empty">
                  <CheckCircle2 size={42} />
                  <h2>JazakAllah khair. You are in the volunteer queue.</h2>
                  <p className="entry-muted">
                    Your contact details are private and visible only to authorized masjid operators/admins. The committee may contact you when an activity matches your skills.
                  </p>
                  {volunteerId && <p className="entry-muted">Reference ID: <strong>{volunteerId}</strong></p>}
                  <div className="entry-actions">
                    <ButtonLink href={`/masjids/${(masjid as any).slug}`} rightIcon={<ArrowRight size={14} />}>Return to profile</ButtonLink>
                    <ButtonLink href="/masjids" variant="secondary">Browse masjids</ButtonLink>
                  </div>
                </div>
              ) : (
                <div className="entry-form">
                  <AIAlert
                    title={`Serving ${(masjid as any).nameEnglish}`}
                    body={`${(masjid as any).district}, ${(masjid as any).city}. Your volunteer information is private and helps the committee coordinate local activities responsibly.`}
                    tone="info"
                  />

                  <div className="entry-field-row">
                    <label className="entry-field">
                      <span>Name *</span>
                      <input className={errors.name ? 'is-invalid' : ''} value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Your full name" />
                      {errors.name && <small className="entry-error">{errors.name}</small>}
                    </label>
                    <label className="entry-field">
                      <span>Mobile *</span>
                      <input className={errors.mobile ? 'is-invalid' : ''} value={form.mobile} onChange={(e) => setField('mobile', e.target.value)} placeholder="+91 98765 43210" />
                      {errors.mobile && <small className="entry-error">{errors.mobile}</small>}
                    </label>
                  </div>

                  <div className="entry-field-row">
                    <label className="entry-field">
                      <span>Email</span>
                      <input className={errors.email ? 'is-invalid' : ''} type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="Optional" />
                      {errors.email && <small className="entry-error">{errors.email}</small>}
                    </label>
                    <label className="entry-field">
                      <span>Locality *</span>
                      <input className={errors.locality ? 'is-invalid' : ''} value={form.locality} onChange={(e) => setField('locality', e.target.value)} placeholder="Tolichowki, Mehdipatnam..." />
                      {errors.locality && <small className="entry-error">{errors.locality}</small>}
                    </label>
                  </div>

                  <div className="entry-field">
                    <span>Skills / interests *</span>
                    <div className="entry-card-grid">
                      {skillOptions.map((skill) => (
                        <button
                          key={skill.value}
                          type="button"
                          className={`entry-list-item ${form.skills.includes(skill.value) ? 'is-active' : ''}`}
                          onClick={() => toggleSkill(skill.value)}
                        >
                          <span className="entry-list-main">
                            <span className="entry-icon-box"><Sparkles size={16} /></span>
                            <span>
                              <strong>{skill.label}</strong>
                              <p className="entry-muted">{skill.helper}</p>
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                    {errors.skills && <small className="entry-error">{errors.skills}</small>}
                  </div>

                  <label className="entry-field">
                    <span>Availability note</span>
                    <textarea value={form.availabilityNote} onChange={(e) => setField('availabilityNote', e.target.value)} placeholder="Weekends, after Maghrib, Ramadan evenings, emergency funeral prayer support, etc." />
                  </label>

                  <label className="entry-field" style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
                    <input type="checkbox" checked={form.consent} onChange={(e) => setField('consent', e.target.checked)} style={{ width: 18, marginTop: 3 }} />
                    <span>I consent to this masjid committee/admin contacting me for volunteer coordination. I understand my details are not public.</span>
                  </label>
                  {errors.consent && <small className="entry-error">{errors.consent}</small>}
                  {errors.submit && <div className="entry-error">{errors.submit}</div>}

                  <div className="entry-actions">
                    <Button onClick={() => requireAuth({ onAuthed: submit, label: 'Create a free account to register as a volunteer.' })} disabled={submitting} rightIcon={<CheckCircle2 size={15} />}>{submitting ? 'Submitting...' : 'Register as volunteer'}</Button>
                    <ButtonLink href={`/masjids/${(masjid as any).slug}`} variant="outline">Cancel</ButtonLink>
                  </div>
                </div>
              )}
            </Card>
          </section>

          <aside className="entry-help-rail">
            <Card padding="md">
              <div className="entry-card-head">
                <div>
                  <Badge tone="neutral">Private volunteer intake</Badge>
                  <h3 className="card-title">{(masjid as any).nameEnglish}</h3>
                </div>
                <ShieldCheck size={18} />
              </div>
              <div className="entry-list">
                {[
                  { icon: MapPin, title: 'Local service', body: `${(masjid as any).district}, ${(masjid as any).city}` },
                  { icon: Users, title: 'Committee follow-up', body: 'Authorized operators can contact and assign volunteers.' },
                  { icon: Phone, title: 'Privacy', body: 'Your mobile/email are never listed publicly.' },
                  { icon: HandHeart, title: 'Activities', body: 'Events, cleaning, Ramadan support, funeral prayer support, teaching, media, and more.' },
                ].map((item) => {
                  const Icon = item.icon
                  return (
                    <div key={item.title} className="entry-list-main">
                      <span className="entry-icon-box"><Icon size={17} /></span>
                      <div>
                        <strong>{item.title}</strong>
                        <p className="entry-muted">{item.body}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </Card>
          </aside>
        </div>
      </div>
    </main>
  )
}
